import React, { FormEvent, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import ReCAPTCHA from 'react-google-recaptcha'
import { GoogleReCaptcha } from '../../lib/components'
import { userServices } from '../../services'
import { createConfiguration } from '../../global-state/investor-slice'
import { showBanner } from '../../global-state/banner-slice'
import { Role } from '../../lib/types'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { trackGaSignUp } from '../../lib/analytics/google-analytics'
import posthog from '../../lib/utils/posthogUtils/posthog'
import {
  clearStoredUtmAttribution,
  getStoredUtmAttribution,
  toUtmAttributionPayload,
  toUtmEventProperties,
} from '../../lib/utils/utm-attribution'
import { captureEvent } from '../../lib/utils/posthogUtils/usePostHog'
import { EVENTS } from '../../lib/utils/constants'
import { applyPostLoginNavigation } from '../../lib/utils/post-login'
import { GLogin } from '../login/google-login-container'

type PromoteDirectSignupFormProps = {
  formId: string
}

const CAPTCHA_TIMEOUT_MS = 15000

const PromoteDirectSignupForm = ({ formId }: PromoteDirectSignupFormProps) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const reRef = useRef<ReCAPTCHA>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [consent, setConsent] = useState(false)
  // Bot trap. A checkbox, because Chrome autofill fills hidden text inputs (e.g. "website"),
  // which used to make real users' submits silently do nothing.
  const [botTrap, setBotTrap] = useState(false)
  const [message, setMessage] = useState('')
  const [messageKind, setMessageKind] = useState<'ok' | 'err' | ''>('')
  const [submitting, setSubmitting] = useState(false)

  const fail = (text: string) => {
    setMessage(text)
    setMessageKind('err')
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setMessage('')
    setMessageKind('')

    if (botTrap) return
    const trimmedName = name.trim()
    const trimmedEmail = email.trim().toLowerCase()
    if (!trimmedName || !trimmedEmail) {
      return fail('Please fill in your name and email.')
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      return fail('That email does not look right.')
    }
    if (!consent) {
      return fail('Please tick the box to continue.')
    }

    setSubmitting(true)
    setMessage('Creating your account…')

    let token: string | null | undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      // On some mobile browsers the invisible captcha never resolves; do not leave the form stuck.
      token = await Promise.race([
        reRef.current?.executeAsync(),
        new Promise<null>((resolve) => {
          timer = setTimeout(() => resolve(null), CAPTCHA_TIMEOUT_MS)
        }),
      ])
    } catch {
      token = null
    } finally {
      clearTimeout(timer)
    }
    try {
      reRef.current?.reset()
    } catch {
      // reset is best effort; a valid token is still usable
    }

    if (!token) {
      setSubmitting(false)
      return fail('Verification failed, please retry.')
    }

    const utmAttribution = toUtmAttributionPayload(getStoredUtmAttribution())
    const response = await userServices.promoteMagicSignup_v1(
      {
        name: trimmedName,
        email: trimmedEmail,
        consent: true,
        page: window.location.href,
        ...(utmAttribution ? { utmAttribution } : {}),
      },
      { 'g-recaptcha-response': token },
    )

    if (response?.status === 200 && response?.data?.role === Role.investor) {
      const createdUser = response.data
      const investorId: number = createdUser.userId

      dispatch(
        createConfiguration({
          ...createdUser,
          userId: investorId,
          accountStatus: createdUser.account_status,
        }),
      )
      dispatch(showBanner({ text: 'Please verify your email address', variant: 'warning-banner' }))
      logCrmEvent(
        EngagementEvents.USER_SIGNED_UP_EMAIL,
        { Authorization: createdUser.authToken ?? '' },
        { name: createdUser.name, email: createdUser.email },
      )

      const utmEventProperties = toUtmEventProperties(getStoredUtmAttribution())
      captureEvent(EVENTS.user_signed_up, {
        email: trimmedEmail,
        source: 'promote',
        ...utmEventProperties,
      })
      posthog.identify(trimmedEmail, utmEventProperties)
      trackGaSignUp('email', utmEventProperties)
      clearStoredUtmAttribution()

      navigate('/opportunities')
      return
    }

    setSubmitting(false)
    fail((response?.data && response.data.message) || 'Something went wrong. Please try again.')
  }

  const handleGoogleSuccess = async (res: object) => {
    setMessage('')
    setMessageKind('')
    const { access_token: accessToken } = res as { access_token: string }
    const utmAttribution = toUtmAttributionPayload(getStoredUtmAttribution())
    const user = await userServices.googleSignIn_v1({
      accessToken,
      role: Role.investor,
      ...(utmAttribution ? { utmAttribution } : {}),
    })

    if (!(user?.status === 200 && user.data?.role === Role.investor)) {
      return fail(
        (user?.data && (user.data as { message?: string }).message) ||
          'Something went wrong. Please try again.',
      )
    }

    const isNewUser = user.data.isNewUser === true
    const utmEventProperties = toUtmEventProperties(getStoredUtmAttribution())
    dispatch(
      createConfiguration({
        ...user.data,
        accountStatus: user.data.account_status,
        isEmailVerified: user.data.isEmailVerified ?? user.data.is_verified ?? true,
        acceptedNDA: user.data.acceptedNDA ?? false,
        kycStatus: user.data.kycStatus ?? '',
      }),
    )

    if (isNewUser) {
      logCrmEvent(
        EngagementEvents.USER_SIGNED_UP_SOCIAL,
        { Authorization: user.data.authToken ?? '' },
        { name: user.data.name, email: user.data.email },
      )
      captureEvent(EVENTS.user_signed_up, {
        email: user.data.email,
        source: 'promote',
        ...utmEventProperties,
      })
      posthog.identify(user.data.email, utmEventProperties)
      trackGaSignUp('google', utmEventProperties)
      clearStoredUtmAttribution()
      // New Google signups skip onboarding and go straight to the listings.
      navigate('/opportunities')
      return
    }

    // Returning Google users continue the normal onboarding flow.
    logCrmEvent(
      EngagementEvents.USER_LOGGED_IN_SOCIAL,
      { Authorization: user.data.authToken ?? '' },
      { email: user.data.email, socialPlatform: 'google' },
    )
    posthog.identify(user.data.email)
    await applyPostLoginNavigation(
      dispatch,
      navigate,
      user.data.authToken,
      user.data.userId,
      user.data.email,
      user.data.name,
      user.data.acceptedNDA ?? false,
    )
  }

  return (
    <form
      className='promote-page__form'
      id={formId}
      data-testid={`promote-signup-form-${formId}`}
      onSubmit={handleSubmit}
      noValidate
    >
      <div className='promote-page__form-row'>
        <input
          type='text'
          name='name'
          placeholder='Full Name'
          autoComplete='name'
          required
          aria-label='Full Name'
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid={`${formId}-name`}
        />
        <input
          type='email'
          name='email'
          placeholder='Email'
          autoComplete='email'
          required
          aria-label='Email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid={`${formId}-email`}
        />
      </div>
      <label className='promote-page__consent'>
        <input
          type='checkbox'
          name='consent'
          required
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid={`${formId}-consent`}
        />
        <span>
          I agree to receive account emails from Credibila, and I have read the{' '}
          <Link to='/disclaimer'>risk disclosure</Link> and{' '}
          <Link to='/privacy_policy'>privacy policy</Link>.
        </span>
      </label>
      <input
        type='checkbox'
        name='contact_me_by_fax_only'
        className='promote-page__hp'
        tabIndex={-1}
        autoComplete='off'
        aria-hidden='true'
        checked={botTrap}
        onChange={(e) => setBotTrap(e.target.checked)}
      />
      <GoogleReCaptcha handleChange={() => undefined} size='invisible' reCaptchaRef={reRef} />
      <div
        className={`promote-page__msg${
          messageKind === 'err'
            ? ' promote-page__msg--err'
            : messageKind === 'ok'
            ? ' promote-page__msg--ok'
            : ''
        }`}
        role='status'
        aria-live='polite'
        data-testid={`${formId}-msg`}
      >
        {message}
      </div>
      <button
        type='submit'
        className='promote-page__btn'
        disabled={submitting}
        data-testid={`${formId}-submit`}
      >
        Create Account &amp; Invest
      </button>
      <div className='promote-page__tiny'>
        We email you a link to verify your address and a temporary password. Free. No obligation.
      </div>
      <div className='promote-page__or' aria-hidden='true'>
        or
      </div>
      <div className='promote-page__google' data-testid={`${formId}-google`}>
        <GLogin
          buttonText='Sign up with Google'
          onSuccess={handleGoogleSuccess}
          onFailure={() => fail('Google sign up was cancelled. Please try again.')}
        />
      </div>
    </form>
  )
}

export { PromoteDirectSignupForm }
